<template>
  <div class="profile">
    <div class="profile__header container">
      <h1 class="profile__header-title">{{ $t("profile.title") }}</h1>
    </div>

    <div class="profile__data">
      <div class="profile__data-block container">
        <h2 class="profile__data-block-title">
          {{ $t("profile.personalData") }}
        </h2>

        <transition name="fade">
          <div class="profile__data-block__error-text" v-if="!!error">
            {{ error }}
          </div>
        </transition>

        <div class="profile__data-block-form">
          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("common.lastName") }}
              <span class="profile-form__required">*</span>
            </template>

            <input
              type="text"
              name="lastname"
              v-validate="'required'"
              v-model="details.lastname"
            />
            <template slot="error">
              {{ errors.first("lastname") }}
            </template>
          </InputLabel>

          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("common.firstName") }}
              <span class="profile-form__required">*</span>
            </template>

            <input
              type="text"
              v-model="details.name"
              name="name"
              v-validate="'required'"
            />

            <template slot="error">
              {{ errors.first("name") }}
            </template>
          </InputLabel>

          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("common.middleName") }}
            </template>

            <input type="text" v-model="details.middlename" name="middlename" />
          </InputLabel>

          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("common.email") }}
              <span class="profile-form__required">*</span>
            </template>

            <input
              type="text"
              placeholder="your@email.com"
              v-model="details.email"
              disabled
            />
          </InputLabel>

          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("common.phone") }}
            </template>

            <input
              type="text"
              v-mask="'+##################'"
              placeholder="+7 900 000 00 00"
              v-model="details.phone"
            />
          </InputLabel>
        </div>
        <div class="profile__data-block-actions">
          <div>
            <div>
              <PrimaryButton
                @click.prevent="saveProfile"
                :disabled="
                  errors.has('name') ||
                    errors.has('lastname') ||
                    isSavingProfile
                "
                :is-loading="isSavingProfile"
              >
                <template v-if="isSavedSuccessfuly">
                  {{ $t("profile.saveSuccess") }}
                </template>

                <template v-else>
                  {{ $t("profile.save") }}
                </template>
              </PrimaryButton>
            </div>
          </div>

          <span
            style="width: 150px; display: flex; align-items: center;"
            v-if="!isChangePasswordShown"
            @click="isChangePasswordShown = true"
          >
            <LinkIcon
              icon="link-icon-edit"
              class="profile__data-block-form-btn"
            >
              {{ $t("profile.changePassword") }}
            </LinkIcon>
          </span>
        </div>
      </div>

      <form
        @submit.prevent="updatePassword"
        v-show="isChangePasswordShown"
        class="profile__data-block container"
        data-vv-scope="updatePassword"
      >
        <h2 class="profile__data-block-title">
          {{ $t("profile.changePasswordTitle") }}
        </h2>

        <transition name="fade">
          <div class="profile__data-block__error-text" v-if="!!passwordError">
            {{ passwordError }}
          </div>
        </transition>

        <div class="profile__data-block-form">
          <InputLabel class="profile__data-block-form-inp">
            <template #label> {{ $t("profile.oldPassword") }} * </template>

            <input
              type="password"
              v-model="oldPassword"
              v-validate="'required'"
              name="oldPassword"
              :data-vv-as="$t('profile.oldPassword')"
              :disabled="changePasswordSuccess"
            />

            <template slot="error" v-if="changePasswordError">
              {{ errors.first("updatePassword.oldPassword") }}
              {{ changePasswordError }}
            </template>
          </InputLabel>

          <br />

          <InputLabel class="profile__data-block-form-inp">
            <template #label> {{ $t("profile.newPassword") }} * </template>

            <input
              type="password"
              v-model="newPassword"
              v-validate="'required|min:8'"
              name="newPassword"
              ref="newPassword"
              :data-vv-as="$t('profile.newPassword')"
              :disabled="changePasswordSuccess"
            />

            <template slot="error">
              {{ errors.first("updatePassword.newPassword") }}
            </template>
          </InputLabel>

          <InputLabel class="profile__data-block-form-inp">
            <template #label>
              {{ $t("profile.passwordConfirmation") }} *
            </template>

            <input
              type="password"
              name="passwordConfirmation"
              v-model="passwordConfirmation"
              v-validate="'required|confirmed:newPassword'"
              :data-vv-as="$t('profile.passwordConfirmation')"
              :disabled="changePasswordSuccess"
            />

            <template slot="error">
              {{ errors.first("updatePassword.passwordConfirmation") }}
            </template>
          </InputLabel>
        </div>
        <div class="profile__data-block-form_flex">
          <PrimaryButton
            @click.prevent="updatePassword"
            :disabled="
              !oldPassword ||
                !newPassword ||
                !passwordConfirmation ||
                errors.has('updatePassword.oldPassword') ||
                errors.has('updatePassword.passwordConfirmation') ||
                errors.has('updatePassword.newPassword') ||
                isSavingPassword
            "
            :is-loading="isSavingPassword"
          >
            <template v-if="!changePasswordSuccess">
              {{ $t("profile.changePassword") }}
            </template>

            <template v-else>
              {{ $t("profile.changePasswordSuccess") }}
            </template>
          </PrimaryButton>

          <button
            class="profile__data-block-btn-cancel"
            @click.prevent="cancelUpdatePassword"
            v-if="!changePasswordSuccess"
          >
            {{ $t("common.cancelBtn") }}
          </button>
        </div>
      </form>

      <div class="profile__data-block container">
        <h2 class="profile__data-block-title">
          {{ $t("notification.emailNotification") }}
        </h2>
        <div class="email-notifications">
          <div class="email-notifications__item">
            <Switcher v-model="details.certificate_email_notify">
              {{
                $t("notification.emailNotifications.certificate_email_notify")
              }}
            </Switcher>
          </div>
          <div class="email-notifications__item">
            <Switcher v-model="details.installation_email_notify">
              {{
                $t("notification.emailNotifications.installation_email_notify")
              }}
            </Switcher>
          </div>
          <div class="email-notifications__item">
            <Switcher v-model="details.car_pass_email_notify">
              {{ $t("notification.emailNotifications.car_pass_email_notify") }}
            </Switcher>
          </div>
          <div class="email-notifications__item">
            <Switcher v-model="details.catalog_order_email_notify">
              {{
                $t("notification.emailNotifications.catalog_order_email_notify")
              }}
            </Switcher>
          </div>
          <div class="email-notifications__item">
            <Switcher v-model="details.document_email_notify">
              {{ $t("notification.emailNotifications.document_email_notify") }}
            </Switcher>
          </div>
          <div class="email-notifications__item">
            <Switcher v-model="details.additional_order_email_notify">
              {{ $t("notification.emailNotifications.additional_services") }}
            </Switcher>
          </div>
        </div>

        <PrimaryButton
          :disabled="isSavingNotification"
          @click="emailNotificationsUpdate"
          :is-loading="isSavingNotification"
        >
          <template v-if="isNotificationSucces">
            {{ $t("profile.saveSuccess") }}
          </template>

          <template v-else>
            {{ $t("profile.save") }}
          </template>
        </PrimaryButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import apiUser from "@/api/user";
import getError from "@/helpers/error";
import {
  PrimaryButton,
  LinkIcon,
  Switcher,
  InputLabel
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";

export default {
  components: {
    InputLabel,
    LinkIcon,
    PrimaryButton,
    Switcher
  },

  data() {
    return {
      isSavingProfile: false,
      isSavingPassword: false,
      isSavingNotification: false,
      details: {},
      isEmailChangeEnabled: false,

      isChangePasswordShown: false,

      oldPassword: "",
      newPassword: "",
      passwordConfirmation: "",
      changePasswordError: "",
      changePasswordSuccess: false,

      isFormDirty: false,

      isSavedSuccessfuly: false,
      isNotificationSucces: false,
      error: "",
      passwordError: ""
    };
  },

  computed: {
    ...mapGetters("auth", ["user"]),

    locale() {
      return i18n.locale;
    }
  },

  methods: {
    saveProfile() {
      this.$validator.validateAll().then(result => {
        if (!result) {
          return;
        }
        this.isSavingProfile = true;

        apiUser
          .update({
            lastname: this.details.lastname,
            name: this.details.name,
            middlename: this.details.middlename,
            phone: this.details.phone
              ? this.details.phone.replace(/[\s+]/gi, "")
              : ""
          })
          .then(data => {
            this.error = "";
            if (data && data.data) {
              this.$store.dispatch("auth/setUser", data.data);
              this.$nextTick(() => {
                this.isFormDirty = false;
                this.isSavingProfile = false;

                this.$validator.reset();
              });
              this.isSavedSuccessfuly = true;
              setTimeout(() => {
                this.isSavedSuccessfuly = false;
              }, 3000);
            }
          })
          .catch(response => {
            this.isSavingProfile = false;
            this.error = getError(response);
          });
      });
    },
    emailNotificationsUpdate() {
      this.isSavingNotification = true;
      apiUser
        .emailNotificationsUpdate({
          certificate_email_notify: this.details.certificate_email_notify,
          installation_email_notify: this.details.installation_email_notify,
          car_pass_email_notify: this.details.car_pass_email_notify,
          catalog_order_email_notify: this.details.catalog_order_email_notify,
          document_email_notify: this.details.document_email_notify,
          additional_order_email_notify: this.details
            .additional_order_email_notify
        })
        .then(data => {
          this.$store.dispatch("auth/setUser", data.data);
          this.isNotificationSucces = true;
          this.isSavingNotification = false;
          setTimeout(() => {
            this.isNotificationSucces = false;
          }, 3000);
        })
        .catch(() => {
          this.isSavingNotification = false;
        });
    },

    updatePassword() {
      this.$validator.validateAll("updatePassword").then(result => {
        if (!result) {
          return;
        }
        this.isSavingPassword = true;
        apiUser
          .changePassword(
            this.oldPassword,
            this.newPassword,
            this.passwordConfirmation
          )
          .then(data => {
            this.passwordError = "";
            if (data && data.token) {
              this.$store.commit("auth/UPDATE_TOKEN", data.token);
            }
            this.changePasswordError = "";
            this.changePasswordSuccess = true;
            this.isSavingPassword = false;
            setTimeout(() => {
              this.cancelUpdatePassword();
            }, 3000);
          })
          .catch(response => {
            this.isSavingPassword = false;
            this.passwordError = getError(response);
          });
      });
    },

    cancelUpdatePassword() {
      this.oldPassword = "";
      this.newPassword = "";
      this.passwordConfirmation = "";

      this.changePasswordError = "";
      this.changePasswordSuccess = false;
      this.isChangePasswordShown = false;

      this.errors.clear("updatePassword");
    }
  },

  mounted() {
    this.details = Object.assign({}, this.user);
    this.errors.clear();
  },

  watch: {
    details: {
      deep: "true",
      handler() {
        this.isFormDirty = true;
      }
    },

    oldPassword() {
      this.changePasswordError = "";
    },

    newPassword() {
      this.changePasswordError = "";
    },

    passwordConfirmation() {
      this.changePasswordError = "";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.profile {
  &__header {
    box-sizing: border-box;
    padding: 38px 0 48px 0;

    @include respond-down(md) {
      padding: 24px 0;
    }

    &-title {
      @include h1();

      @include respond-down(md) {
        font-size: 20px;
      }
    }
  }
  &-form__required {
    color: var(--orange, #f6931d);
  }
  &__data {
    background-color: #f9f9f9;
    padding: 41px 0 24px 0;

    @include respond-down(md) {
      padding: 16px 0;
    }

    &-block {
      background-color: #fff;
      padding: 31px 0 32px 80px;
      margin-bottom: 23px;
      box-sizing: border-box;

      border: 1px solid var(--gray-darker);

      @include respond-down(lg) {
        padding-left: 32px;
      }

      @include respond-down(md) {
        width: calc(100% + 16px);

        margin-left: -16px;
        padding: 20px 16px 20px 32px;
      }

      &-title {
        margin: 13px 0 32px 0;

        font-size: 21px;
        font-weight: 500;

        line-height: 1.14;

        color: #262626;

        @include respond-down(md) {
          font-size: 16px;
          margin-top: 0;
          margin-bottom: 16px;
        }
      }

      &__error-text {
        font-size: 12px;
        color: #ea5330;
      }

      &-form {
        margin-bottom: 8px;
        &_flex {
          display: flex;
          align-items: center;
        }

        &-inp {
          width: 250px;
          display: inline-block;
          vertical-align: top;
          margin: 0 20px 32px 0;

          @include respond-down(lg) {
            width: 205px;
          }

          @include respond-down(md) {
            width: 100%;

            margin-right: 0;
            margin-bottom: 16px;
          }

          &[disabled] {
            background-color: rgba(241, 241, 241, 0.5) !important;
          }

          &-btn {
            position: absolute;
            top: 40px;
            right: 16px;

            font-size: 13px;

            padding: 0;

            color: #6d7179;
            background: none;
            border: none;
            outline: none;
            border-bottom: 1px solid #a3a8b3;

            cursor: pointer;

            @include respond-down(md) {
              top: 38px;
              font-size: 12px;
            }
          }
        }

        &-btn {
          margin: 2px 0 0 -1px;
        }
      }

      &-actions {
        display: flex;
        align-content: center;
        justify-content: space-between;
        margin-right: 70px;

        @include respond-down(lg) {
          margin-right: 35px;
        }

        @include respond-down(md) {
          margin-right: 0;
        }
      }

      &-btn-cancel {
        font-size: 13px;

        padding: 0;
        margin-left: 32px;
        // margin-bottom: 40px;

        color: #6d7179;
        background: none;
        border: none;
        outline: none;
        border-bottom: 1px solid #a3a8b3;

        cursor: pointer;
      }
    }
  }
}

.email-notifications {
  padding-right: 16px;
  margin-bottom: 25px;

  @include respond-up(md) {
    padding-right: 32px;
  }
  @include respond-up(lg) {
    column-count: 2;
    column-gap: 100px;
    padding-right: 158px;
  }
  &__item {
    margin-bottom: 24px;
  }
}
</style>
